import ScienceIcon from '@mui/icons-material/Science';
import { Button, Chip, Stack } from '@mui/material';
import {
  buildNucleaiOriginsRouteState,
  buildNucleaiOriginsSearch,
  NucleaiOriginsRouteState,
} from 'components/Pages/NucleaiOrigins/navigation';
import { Permission } from 'interfaces/permissionOption';
import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { usePermissions } from 'utils/usePermissions';

import { NUCLEAI_ORIGINS_ROUTE_PATH } from 'components/Pages/NucleaiOrigins/navigation';

const NUCLEAI_ORIGINS_BADGE_LABEL = 'Alpha';
const NUCLEAI_ORIGINS_ACCESSIBLE_LABEL = 'Nucleai Origins Alpha';

interface Props {
  iconOnly?: boolean;
}

const NucleaiOriginsButton: React.FC<Props> = ({ iconOnly = false }) => {
  const location = useLocation();
  const { labId } = useCurrentLabId();
  const { hasPermission } = usePermissions();
  const canAccessOrigins = hasPermission(Permission.ApplyPermissionsAcrossLabs);

  const isActive = location.pathname === NUCLEAI_ORIGINS_ROUTE_PATH;
  const routeState = React.useMemo(
    () =>
      isActive
        ? (location.state as NucleaiOriginsRouteState | null) ?? undefined
        : buildNucleaiOriginsRouteState({ location, labId }),
    [isActive, labId, location]
  );
  const routeSearch = isActive ? location.search : buildNucleaiOriginsSearch(labId);

  if (!canAccessOrigins) {
    return null;
  }

  return (
    <Button
      component={RouterLink}
      to={{ pathname: NUCLEAI_ORIGINS_ROUTE_PATH, search: routeSearch }}
      state={routeState}
      color="secondary"
      variant={isActive ? 'contained' : 'outlined'}
      aria-label={NUCLEAI_ORIGINS_ACCESSIBLE_LABEL}
      title={iconOnly ? NUCLEAI_ORIGINS_ACCESSIBLE_LABEL : undefined}
      startIcon={iconOnly ? undefined : <ScienceIcon />}
      sx={{
        textTransform: 'none',
        whiteSpace: 'nowrap',
        gap: iconOnly ? 0 : 0.5,
        borderRadius: 999,
        minWidth: 0,
        px: iconOnly ? 1.5 : 2,
        fontWeight: 600,
      }}
    >
      {iconOnly ? (
        <ScienceIcon fontSize="small" />
      ) : (
        <Stack component="span" direction="row" spacing={1} alignItems="center">
          <span>Nucleai Origins</span>
          <Chip
            component="span"
            label={NUCLEAI_ORIGINS_BADGE_LABEL}
            size="small"
            color="warning"
            variant="outlined"
            sx={{
              height: 20,
              borderRadius: 999,
              '& .MuiChip-label': {
                px: 1,
                fontSize: '0.65rem',
                fontWeight: 700,
                lineHeight: 1,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              },
            }}
          />
        </Stack>
      )}
    </Button>
  );
};

export default NucleaiOriginsButton;
